import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useClerkAuth } from '../../../hooks/useClerkAuth';
import { RefreshCw, Send, Monitor, CheckSquare, Square, Zap, ChevronLeft, ChevronRight, Home, Search, X } from 'lucide-react';
import { useToast } from '../../ui/Toast';
import Button from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import { DesignCardSkeleton } from '../../ui/SkeletonLoader';
import { CanvaNotConnectedEmpty, NoDesignsEmpty, NoSearchResultsEmpty } from '../../ui/EmptyState';
import DesignGrid from './DesignGrid';
import FolderSection from './FolderSection';
import BentoGrid, { BentoItem } from '../../layouts/BentoGrid';
import AgentPickerPopover from '../../ui/AgentPickerPopover';
import SyncActivityBanner from './SyncActivityBanner';
import CanvaAuthBanner from './CanvaAuthBanner';
import { haptics } from '../../../utils/haptics';
import { searchDesignsAcrossAccount } from '../../../services/designSearch';
import { findCanvaAccount, unlinkCanva } from '../../../services/canvaLink';

const ROOT_FOLDER = { id: 'root', name: 'My Designs' };
const PAGE_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Dashboard Component
 *
 * Server-paginated design browser. n8n's fetch-designs webhook returns one
 * Canva page per call (25 items by default), so navigation is first/prev/next
 * via continuation tokens — no arbitrary page-jump (that would require walking
 * all prior tokens from page 0).
 *
 * Folder navigation rides on the same primitive: clicking a folder card
 * re-fetches with `folderId` set to that folder.
 *
 * `selectedDesigns` (in AppContext) is keyed by design_id, so selection
 * persists across pages and folder transitions.
 */
export default function Dashboard() {
  const { user } = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasCanvaConnected } = useClerkAuth();
  const {
    state, isApiReady, fetchDesigns, fetchLinkedAgents,
    clearError, clearCanvaReauth, selectAllDesigns, clearSelection,
    toggleDesignSelection, sendDesignToAgent, loadInProPresenter, triggerSyncNow,
    markDesignCompleted, clearDispatchProgress, markDispatchQueued, markDispatchResolved, markDispatchFailed,
  } = useApp();
  const { designs, isDesignsLoading, error, selectedDesigns, linkedAgents, canvaReauthRequired, isSyncingNow } = state;
  const isCanvaConnected = hasCanvaConnected();

  // folderStack: breadcrumb (root → … → current folder)
  // tokenStack:  tokenStack[i] is the continuation token used to fetch page i.
  //              tokenStack[0] = '' (first page). length always == pageIndex + 1.
  // nextTokenRef holds the continuation token to USE for goNext — it's the
  // `continuation` field returned by the most recent page response.
  // foldersRefreshKey: bumped to force <FolderSection /> to re-fetch (its own
  // 30-min cache busts on a new key — used by the Refresh button + Sync Now).
  const [folderStack, setFolderStack] = useState([ROOT_FOLDER]);
  const [tokenStack, setTokenStack] = useState(['']);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [foldersRefreshKey, setFoldersRefreshKey] = useState(0);
  const nextTokenRef = useRef(null);

  // Search (client-side, account-wide): debouncedSearchQuery drives
  // searchDesignsAcrossAccount, which crawls the whole folder tree (not
  // just the currently-open folder) with bounded concurrency and fuzzy-
  // filters by title. Per product decision, search results are a single
  // page — no next/prev over the filtered set.
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Session-lived caches passed into searchDesignsAcrossAccount so refining
  // a query (e.g. "a" → "am" → "ama") skips network calls for folders/pages
  // already seen this session.
  const searchFolderCacheRef = useRef(new Map());
  const searchDesignPageCacheRef = useRef(new Map());
  // Every design object we've ever seen (normal pagination or search), keyed
  // by id — lets dispatch actions resolve a selected design's title even
  // when the selection was made from search results that aren't on the
  // currently-loaded server page.
  const knownDesignsRef = useRef(new Map());

  const onlineAgents = linkedAgents.filter(agent => agent.status === 'online');
  const selectedCount = selectedDesigns.length;
  const isSearchActive = debouncedSearchQuery.length > 0;
  const displayedDesigns = isSearchActive ? searchResults : designs;
  const displayedLoading = isSearchActive ? isSearching : isDesignsLoading;
  const selectedOnPage = selectedDesigns.filter(id => displayedDesigns.some(d => (d.design_id || d.id) === id)).length;
  const allOnPageSelected = displayedDesigns.length > 0 && selectedOnPage === displayedDesigns.length;
  const currentFolder = folderStack[folderStack.length - 1];
  const currentFolderId = currentFolder.id;

  // Single page-fetch helper. Always captures the response's continuation
  // token into nextTokenRef so goNext() has it when the user clicks.
  const loadPage = useCallback(async ({ folderId, continuation, forceRefresh = false } = {}) => {
    const result = await fetchDesigns({
      folderId,
      continuation: continuation || '',
      limit: PAGE_LIMIT,
      forceRefresh,
    });
    if (!result) {
      nextTokenRef.current = null;
      return null;
    }
    setHasMore(!!result.hasMore);
    nextTokenRef.current = result.continuation || null;
    return result;
  }, [fetchDesigns]);

  // First load + agents refresh on Canva-connect
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (isCanvaConnected && isApiReady && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      loadPage({ folderId: 'root', continuation: '' });
      fetchLinkedAgents();
    }
  }, [isCanvaConnected, isApiReady, loadPage, fetchLinkedAgents]);

  useEffect(() => {
    designs.forEach(d => knownDesignsRef.current.set(d.design_id || d.id, d));
  }, [designs]);

  useEffect(() => {
    searchResults.forEach(d => knownDesignsRef.current.set(d.design_id || d.id, d));
  }, [searchResults]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery === debouncedSearchQuery) return undefined;
    const handle = setTimeout(() => {
      setDebouncedSearchQuery(trimmedQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchQuery, debouncedSearchQuery]);

  useEffect(() => {
    if (!debouncedSearchQuery || !isApiReady || !isCanvaConnected) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);
    setSearchResults([]);

    searchDesignsAcrossAccount({
      query: debouncedSearchQuery,
      limit: PAGE_LIMIT,
      folderCache: searchFolderCacheRef.current,
      designPageCache: searchDesignPageCacheRef.current,
      onProgress: (matches) => {
        if (!cancelled) setSearchResults(matches);
      },
      shouldStop: () => cancelled,
    })
      .catch((err) => {
        if (!cancelled) {
          console.error('Design search failed:', err);
          toast.error('Search failed. Please try again.');
          setSearchResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => { cancelled = true; };
    // toast intentionally omitted — it's a new object reference on every
    // ToastProvider render, and including it would re-run the account crawl
    // on unrelated app state changes (e.g. dispatch-progress ticks).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, isApiReady, isCanvaConnected]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, toast, clearError]);

  useEffect(() => {
    if (!canvaReauthRequired) return;

    const handleReauth = async () => {
      if (findCanvaAccount(user)) {
        try { await unlinkCanva(user, { markDisconnected: true }); } catch (err) { console.error('Failed to remove Canva account:', err); }
        try { await user.reload(); } catch (err) { console.error('Failed to reload user after Canva disconnect:', err); }
      }

      clearCanvaReauth();
      toast.warning('Your Canva connection has expired. Please reconnect to continue.');
      navigate('/settings', { state: { reauthRequired: true } });
    };

    handleReauth();
  }, [canvaReauthRequired]);

  // ---- Navigation handlers ----

  const enterFolder = useCallback(async (folder) => {
    haptics.light();
    clearSelection();
    setSearchQuery('');
    const newStack = [...folderStack, { id: folder.folder_id, name: folder.name }];
    setFolderStack(newStack);
    setTokenStack(['']);
    setPageIndex(0);
    await loadPage({ folderId: folder.folder_id, continuation: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [folderStack, loadPage, clearSelection]);

  const navigateToBreadcrumb = useCallback(async (idx) => {
    if (idx >= folderStack.length - 1) return;
    haptics.light();
    clearSelection();
    setSearchQuery('');
    const newStack = folderStack.slice(0, idx + 1);
    setFolderStack(newStack);
    setTokenStack(['']);
    setPageIndex(0);
    await loadPage({ folderId: newStack[newStack.length - 1].id, continuation: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [folderStack, loadPage, clearSelection]);

  const goNext = useCallback(async () => {
    if (!hasMore || isDesignsLoading) return;
    const nextToken = nextTokenRef.current;
    if (!nextToken) return;
    haptics.light();
    setTokenStack(prev => [...prev, nextToken]);
    setPageIndex(prev => prev + 1);
    await loadPage({ folderId: currentFolderId, continuation: nextToken });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [hasMore, isDesignsLoading, currentFolderId, loadPage]);

  const goPrev = useCallback(async () => {
    if (pageIndex === 0 || isDesignsLoading) return;
    haptics.light();
    const newTokenStack = tokenStack.slice(0, -1);
    const newPageIndex = pageIndex - 1;
    setTokenStack(newTokenStack);
    setPageIndex(newPageIndex);
    await loadPage({
      folderId: currentFolderId,
      continuation: newTokenStack[newPageIndex] || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageIndex, isDesignsLoading, tokenStack, currentFolderId, loadPage]);

  const goFirst = useCallback(async () => {
    if (pageIndex === 0 || isDesignsLoading) return;
    haptics.light();
    setTokenStack(['']);
    setPageIndex(0);
    await loadPage({ folderId: currentFolderId, continuation: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageIndex, isDesignsLoading, currentFolderId, loadPage]);

  const handleRefresh = async () => {
    try {
      await loadPage({
        folderId: currentFolderId,
        continuation: tokenStack[pageIndex] || '',
        forceRefresh: true,
      });
      setFoldersRefreshKey(k => k + 1);
      toast.success('Designs refreshed');
    } catch {
      toast.error('Failed to refresh designs');
    }
  };

  const handleSyncNow = async () => {
    haptics.light();
    try {
      await triggerSyncNow();
      toast.success('Sync started — your designs will refresh shortly');
      setTimeout(() => {
        loadPage({
          folderId: currentFolderId,
          continuation: tokenStack[pageIndex] || '',
          forceRefresh: true,
        });
        setFoldersRefreshKey(k => k + 1);
      }, 3000);
    } catch {
      toast.error('Failed to start sync');
    }
  };

  const handleReviewFlagged = (designId) => {
    const el = document.querySelector(`[data-design-id="${designId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSelectAll = () => {
    haptics.light();
    if (allOnPageSelected) {
      clearSelection();
    } else if (isSearchActive) {
      clearSelection();
      displayedDesigns.forEach(d => toggleDesignSelection(d.design_id || d.id));
    } else {
      selectAllDesigns();
    }
  };

  const handleSendToAgent = () => {
    if (selectedCount === 0) return;
    if (onlineAgents.length === 0) {
      toast.error('No online Media Stations available. Make sure at least one Media Station is running and connected.');
      return;
    }
    haptics.light();
    setActionType('send');
    setShowAgentModal(true);
  };

  const handleLoadInProPresenter = () => {
    if (selectedCount === 0) return;
    if (onlineAgents.length === 0) {
      toast.error('No online Media Stations available. Make sure at least one Media Station is running and connected.');
      return;
    }
    haptics.light();
    setActionType('load');
    setShowAgentModal(true);
  };

  // Completion is driven by the n8n webhook HTTP response — the authoritative
  // "process finished" signal (both webhooks resolve in ~6–10s with a 200). We
  // simply await the calls: for "load", assets are sent, then loaded, and the
  // card flips to "Sent!" when the LOAD response resolves; for "send", when the
  // send response resolves. A non-2xx or timeout throws → the catch surfaces
  // Retry. Ably events only animate the intermediate progress bar (see
  // AppContext); they never gate completion.
  const dispatchOne = async (design, agentId, currentActionType) => {
    const designId = design.design_id || design.id;
    const designTitle = design.title || 'Untitled Design';
    // Optimistic seed: render the in-progress overlay on the card immediately.
    // n8n's validation + setup nodes add 1–3s before the first Ably event arrives.
    markDispatchQueued(designId, agentId);
    try {
      if (currentActionType === 'send') {
        await sendDesignToAgent(designId, designTitle, agentId);
      } else if (currentActionType === 'load') {
        await sendDesignToAgent(designId, designTitle, agentId);
        await loadInProPresenter(designId, designTitle, agentId);
      }
      // The webhook HTTP response is the sole authority on resolution. Lock the
      // pair as terminal BEFORE clearing, so a straggler Ably `dispatch_progress`
      // event (delivered after this response — observed live) can't resurrect a
      // "Sending" overlay on the now-completed card.
      markDispatchResolved(designId, agentId);
      markDesignCompleted(designId);
      clearDispatchProgress(designId, agentId);
    } catch (error) {
      // Surface Retry (not a silent clear) so a dropped/timed-out request or a
      // Media Station failure is actionable rather than a frozen card. Lock the
      // pair as terminal first so a late Ably event can't override the Retry.
      markDispatchResolved(designId, agentId);
      markDispatchFailed(designId, agentId, error.message);
      toast.error(`Failed to dispatch "${design.title}": ${error.message}`);
      haptics.error();
    }
  };

  // DEV-only handle so Cypress can drive the real dispatchOne completion logic
  // (Send→Load chain + event-vs-HTTP race) deterministically, without a live
  // Ably connection. Mirrors AppContext's __turboSyncTestDispatch. The ref keeps
  // the exposed function pointing at the latest closure. Never ships to prod.
  const dispatchOneRef = useRef();
  dispatchOneRef.current = dispatchOne;
  useEffect(() => {
    if (!import.meta.env.DEV) return undefined;
    window.__turboSyncTestDispatchOne = (design, agentId, action) =>
      dispatchOneRef.current(design, agentId, action);
    return () => { delete window.__turboSyncTestDispatchOne; };
  }, []);

  const handleAgentSelect = async (agentId) => {
    // Look up via knownDesignsRef, not `designs` — a selection made from
    // search results may not be on the currently-loaded server page.
    const selectedDesignsList = selectedDesigns
      .map(id => knownDesignsRef.current.get(id))
      .filter(Boolean);

    const currentActionType = actionType;
    setShowAgentModal(false);
    setActionType(null);
    clearSelection();

    // Announce the queueing intent BEFORE awaiting the dispatch pipeline.
    // The dispatch chain (especially Send→Load) can take 10–20s end-to-end;
    // surfacing the toast at dispatch start matches user expectations.
    const count = selectedDesignsList.length;
    const verb = currentActionType === 'load' ? 'queued for ProPresenter' : 'queued for Media Station';
    toast.success(`${count} design${count !== 1 ? 's' : ''} ${verb} — track progress on each card.`);
    haptics.success();

    for (const design of selectedDesignsList) {
      await dispatchOne(design, agentId, currentActionType);
    }
  };

  const handleRetryStation = async (design, agentId) => {
    const verb = actionType === 'load' ? 'load' : 'send';
    await dispatchOne(design, agentId, verb);
  };

  const paginationControls = (!isSearchActive && (pageIndex > 0 || hasMore)) ? (
    <div className="flex items-center justify-center gap-2" data-testid="pagination-controls">
      <Button
        onClick={goFirst}
        disabled={pageIndex === 0 || isDesignsLoading}
        variant="secondary"
        size="sm"
        aria-label="First page"
      >
        « First
      </Button>
      <Button
        onClick={goPrev}
        disabled={pageIndex === 0 || isDesignsLoading}
        variant="secondary"
        size="sm"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Prev
      </Button>
      <span className="px-3 text-body-sm text-foreground-secondary">
        Page {pageIndex + 1}{hasMore ? '+' : ''}
      </span>
      <Button
        onClick={goNext}
        disabled={!hasMore || isDesignsLoading}
        variant="secondary"
        size="sm"
        aria-label="Next page"
      >
        Next <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  ) : null;

  return (
    <div className="min-h-screen pb-4 safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero Section */}
        <BentoGrid className="mb-6">
          <BentoItem size="full">
            <Card variant="glass" className="bg-gradient-primary p-8 border-0">
              <CardContent>
                <h1 className="text-display text-white mb-2">
                  Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
                </h1>
                <p className="text-body text-white/80 mb-6">
                  {isCanvaConnected
                    ? 'Select designs to deliver to your media stations'
                    : 'Connect your Canva account to get started'}
                </p>

                {/* Stats reflect live Canva data — hide them when disconnected so
                    the header never shows stale design / Media Station counts. */}
                {isCanvaConnected && (
                  <div className="flex gap-4 text-white/90">
                    <div>
                      <div className="text-heading-xl font-bold">
                        {isDesignsLoading && designs.length === 0 ? (
                          <span className="inline-block w-6 h-7 rounded shimmer bg-white/20" />
                        ) : designs.length}
                      </div>
                      <div className="text-body-sm">Designs on this page</div>
                    </div>
                    <div>
                      <div className="text-heading-xl font-bold">{onlineAgents.length}</div>
                      <div className="text-body-sm">Active Media Stations</div>
                    </div>
                    <div>
                      <div className="text-heading-xl font-bold">{selectedDesigns.length}</div>
                      <div className="text-body-sm">Selected</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </BentoItem>
        </BentoGrid>

        {/* Design Search */}
        {isCanvaConnected && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search hymns by title…"
                aria-label="Search designs by title"
                data-testid="design-search-input"
                className="w-full pl-10 pr-9 py-2.5 squircle-sm bg-gray-100 dark:bg-elevated-dark-2 border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 text-body-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  data-testid="design-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Canva Auth Health Banner */}
        <CanvaAuthBanner />

        {/* Sync Activity Banner */}
        {isCanvaConnected && <SyncActivityBanner onReview={handleReviewFlagged} />}

        {/* Canva Not Connected */}
        {!isCanvaConnected && (
          <CanvaNotConnectedEmpty
            onAction={
              <Button
                onClick={() => navigate('/settings?action=link-canva')}
                variant="primary"
                size="lg"
              >
                Connect Canva Account
              </Button>
            }
          />
        )}

        {/* Breadcrumb */}
        {isCanvaConnected && folderStack.length > 1 && (
          <nav
            aria-label="Folder breadcrumb"
            className="flex items-center flex-wrap gap-1 mb-4 text-body-sm"
            data-testid="folder-breadcrumb"
          >
            {folderStack.map((seg, idx) => {
              const isLast = idx === folderStack.length - 1;
              return (
                <React.Fragment key={`${seg.id}:${idx}`}>
                  <button
                    type="button"
                    onClick={() => !isLast && navigateToBreadcrumb(idx)}
                    disabled={isLast}
                    className={
                      'inline-flex items-center gap-1 px-2 py-1 rounded-md ' +
                      (isLast
                        ? 'text-foreground font-semibold cursor-default'
                        : 'text-foreground-secondary hover:text-foreground hover:bg-foreground/5')
                    }
                  >
                    {idx === 0 && <Home className="w-3.5 h-3.5" />}
                    <span className="truncate max-w-[14rem]">{seg.name}</span>
                  </button>
                  {!isLast && <ChevronRight className="w-3.5 h-3.5 text-foreground-tertiary" />}
                </React.Fragment>
              );
            })}
          </nav>
        )}

        {/* Folder Section — always shown above the design grid when connected */}
        {isCanvaConnected && (
          <FolderSection
            folderId={currentFolderId}
            refreshKey={foldersRefreshKey}
            onEnterFolder={enterFolder}
          />
        )}

        {/* Loading State */}
        {isCanvaConnected && !isSearchActive && isDesignsLoading && designs.length === 0 && (
          <div
            data-testid="designs-loading-skeleton"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
          >
            {[...Array(9)].map((_, i) => (
              <DesignCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {isCanvaConnected && !isSearchActive && !isDesignsLoading && designs.length === 0 && (
          <NoDesignsEmpty
            onAction={
              <Button
                onClick={() => window.open('https://www.canva.com', '_blank')}
                variant="primary"
                size="lg"
              >
                Open Canva
              </Button>
            }
          />
        )}

        {/* No Search Results */}
        {isCanvaConnected && isSearchActive && !isSearching && searchResults.length === 0 && (
          <NoSearchResultsEmpty
            query={debouncedSearchQuery}
            onAction={
              <Button onClick={() => setSearchQuery('')} variant="secondary" size="lg">
                Clear search
              </Button>
            }
          />
        )}

        {/* Designs Grid (designs only — folders are above) */}
        {isCanvaConnected && (displayedDesigns.length > 0 || (isSearchActive && isSearching)) && (
          <>
            {/* Action Buttons */}
            <div className="mb-6 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleSelectAll}
                  disabled={!isCanvaConnected || displayedDesigns.length === 0 || displayedLoading}
                  variant="secondary"
                  size="sm"
                  title={allOnPageSelected ? 'Deselect designs' : 'Select all designs'}
                >
                  {allOnPageSelected ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                  {allOnPageSelected ? (isSearchActive ? 'Deselect Results' : 'Deselect Page') : (isSearchActive ? 'Select Results' : 'Select Page')}
                </Button>
                <Button
                  onClick={handleSyncNow}
                  disabled={isSyncingNow}
                  loading={isSyncingNow}
                  variant="secondary"
                  size="sm"
                  title="Pull latest designs from Canva and dispatch any flagged ones to online Media Stations"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {isSyncingNow ? 'Syncing…' : 'Sync now'}
                </Button>
                <div className="relative">
                  <Button
                    onClick={handleSendToAgent}
                    disabled={selectedCount === 0}
                    variant="primary"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to Media Station ({selectedCount})
                  </Button>
                  <AgentPickerPopover
                    isOpen={showAgentModal && actionType === 'send'}
                    onClose={() => { setShowAgentModal(false); setActionType(null); }}
                    onSelect={handleAgentSelect}
                    agents={onlineAgents}
                    actionLabel="send designs to"
                  />
                </div>
                <div className="relative">
                  <Button
                    onClick={handleLoadInProPresenter}
                    disabled={selectedCount === 0}
                    variant="primary"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    <Monitor className="w-4 h-4 mr-2" />
                    Deliver to ProPresenter ({selectedCount})
                  </Button>
                  <AgentPickerPopover
                    isOpen={showAgentModal && actionType === 'load'}
                    onClose={() => { setShowAgentModal(false); setActionType(null); }}
                    onSelect={handleAgentSelect}
                    agents={onlineAgents}
                    actionLabel="load designs in"
                  />
                </div>
                {!isSearchActive && (
                  <Button
                    onClick={handleRefresh}
                    disabled={!isCanvaConnected}
                    loading={isDesignsLoading}
                    variant="secondary"
                    size="sm"
                    className="ml-auto"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Designs
                  </Button>
                )}
              </div>

              {/* Selection Info */}
              {selectedCount > 0 && (
                <div className="p-4 squircle-sm bg-primary-500/10 dark:bg-primary-400/15 border border-primary-500/30 dark:border-primary-400/30">
                  <p className="text-body-sm text-primary-700 dark:text-primary-200 font-medium">
                    {selectedCount} design{selectedCount !== 1 ? 's' : ''} selected
                    {selectedCount !== selectedOnPage && ` (${selectedOnPage} ${isSearchActive ? 'in these results' : 'on this page'})`}
                  </p>
                </div>
              )}
            </div>

            {paginationControls && (
              <div className="mb-4">{paginationControls}</div>
            )}

            {displayedLoading ? (
              <div
                data-testid="designs-loading-skeleton"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
              >
                {[...Array(9)].map((_, i) => (
                  <DesignCardSkeleton key={i} />
                ))}
              </div>
            ) : (
              <DesignGrid
                designs={displayedDesigns}
                onRetryStation={handleRetryStation}
              />
            )}

            {paginationControls && (
              <div className="mt-6">{paginationControls}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
